import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WindowIcon from "@mui/icons-material/Window";
import { IconTile } from "../../../components/UI/Page";
import { selectUser } from "../../../features/userSlice";
import CustomFormSections from "../../../components/Forms/CustomFormSections";
import {
  formAutoSave,
  hasDraftContent,
} from "../../../components/Forms/Misc/utilities";

// Shared with the Interior Door form so both RFQ forms read as one product.
const CARD_CLASSES = "app-card rounded-2xl border overflow-hidden";

const SECTION_HEADING_CLASSES = "app-eyebrow mb-3";

/*
  Customer, job, and quote/order are chosen on /forms/new/rfq before this form
  opens, so they're shown read-only here rather than re-prompted. Margin is
  deliberately absent — it's internal-only and this form is often on screen
  with the customer present.
*/
const ReadOnlyField = ({ label, value }) => (
  <div>
    <p className="mb-1 block text-sm font-medium">{label}</p>
    <p className="app-surface break-words rounded-xl border px-3 py-2.5 text-sm">
      {value || <span className="app-muted">—</span>}
    </p>
  </div>
);

const CustomWindowForm = ({
  formData,
  setFormData,
  formDefinition,
  onClose,
}) => {
  const user = useSelector(selectUser);
  const [formID, setFormID] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [requiredError, setRequiredError] = useState("");
  const [saving, setSaving] = useState(false);
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    if (!formDefinition?.form_id) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      form_id: formDefinition.form_id,
      form_name: formDefinition.form_name || prev.form_name,
      custom_fields: prev?.custom_fields || {},
    }));
  }, [formDefinition, setFormData]);

  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, []);

  const scheduleAutoSave = (nextData) => {
    // Don't create a draft until something is actually entered.
    if (!hasDraftContent(nextData)) return;
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    autoSaveTimer.current = setTimeout(() => {
      formAutoSave(nextData, setFormID);
    }, 600);
  };

  const setFormDataWithAutoSave = (updater) => {
    setFormData((prev) => {
      const nextData =
        typeof updater === "function" ? updater(prev) : updater;
      scheduleAutoSave(nextData);
      return nextData;
    });
  };

  const updateCustomFields = (updater) => {
    setFormDataWithAutoSave((prev) => {
      const currentFields = prev?.custom_fields || {};
      const nextFields = updater(currentFields);
      return {
        ...prev,
        custom_fields: nextFields,
      };
    });
  };

  const handleFieldChange = (fieldId, value) => {
    updateCustomFields((current) => ({
      ...current,
      [fieldId]: value,
    }));
  };

  const handleSectionChange = (sectionId, rows) => {
    updateCustomFields((current) => ({
      ...current,
      [sectionId]: rows,
    }));
  };

  const buildSubmitUser = (currentUser) => {
    if (!currentUser) {
      return null;
    }
    const name =
      currentUser.name ||
      currentUser.Name ||
      currentUser.username ||
      currentUser.userName;
    const userID = currentUser.userID || currentUser.UserID;
    const email = currentUser.email || currentUser.EmailAddress;
    return {
      name,
      userID,
      email,
    };
  };

  const submitForm = async (payload) => {
    const rawResponse = await fetch("/api/forms/submit", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const content = await rawResponse.json();
    if (content?.success === 1) {
      setSubmitStatus("success");
      setFormID(content.form_id);
    } else {
      setSubmitStatus("error");
    }
  };

  const getMissingRequiredFields = () => {
    const sections = formDefinition?.sections || [];
    if (!sections.length) {
      return [];
    }
    const missingFields = [];
    sections.forEach((section) => {
      const fields = section.fields || [];
      const requiredFields = fields.filter(
        (field) => field?.required && field?.type !== "spacer"
      );
      if (!requiredFields.length) {
        return;
      }
      if (section.type === "repeatable") {
        const rows = Array.isArray(formData?.custom_fields?.[section.id])
          ? formData.custom_fields[section.id]
          : [{}];
        rows.forEach((row, rowIndex) => {
          requiredFields.forEach((field) => {
            const value = row?.[field.id];
            if (
              value === undefined ||
              value === null ||
              String(value).trim() === ""
            ) {
              missingFields.push(
                `${field.label || "Field"} (Row ${rowIndex + 1})`
              );
            }
          });
        });
        return;
      }
      requiredFields.forEach((field) => {
        const value = formData?.custom_fields?.[field.id];
        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ""
        ) {
          missingFields.push(field.label || "Field");
        }
      });
    });
    return missingFields;
  };

  const handleSubmit = async () => {
    const missingFields = getMissingRequiredFields();
    if (missingFields.length) {
      setRequiredError(
        `Please fill the required fields: ${missingFields.join(", ")}.`
      );
      setSubmitStatus(null);
      return;
    }
    setRequiredError("");
    const payload = {
      ...formData,
      form_id: formDefinition?.form_id || formData.form_id,
      form_name: formDefinition?.form_name || formData.form_name,
      entry_date: new Date(),
      submit_user: buildSubmitUser(user),
      submitted: 1,
      status: formData.status || "Submitted",
    };
    await submitForm(payload);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    await formAutoSave(formData, setFormID);
    setSaving(false);
  };

  const formName = formDefinition?.form_name || "Custom Window Form";
  const hasMessages = !!(submitStatus || requiredError || formID);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 text-[color:var(--app-text)] sm:px-6">
      <button
        type="button"
        onClick={() => onClose?.()}
        className="app-btn app-btn-secondary app-btn-sm mb-5"
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        Back to Form Selection
      </button>

      {/* ── Header ── */}
      <div className={`${CARD_CLASSES} mb-5`}>
        <div className="app-divider flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <IconTile icon={WindowIcon} tone="sky" />
            <div className="min-w-0">
              <p className="app-eyebrow">Forms</p>
              <h1 className="text-xl font-bold tracking-tight">
                {formName} {formData.form_type?.toUpperCase() || "QUOTE"}
              </h1>
              <p className="app-muted mt-0.5 truncate text-sm">
                {formData.customer || "No Customer"}{" "}
                {formData.job ? `| Job #${formData.job}` : ""}
              </p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
            Auto-saving
          </span>
        </div>

        {/* ── Section: Customer & Job (read-only) ── */}
        <div className="px-5 py-5 sm:px-6">
          <h2 className={SECTION_HEADING_CLASSES}>Customer &amp; Job</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ReadOnlyField label="Customer" value={formData.customer} />
            <ReadOnlyField label="Account Number" value={formData.account} />
            <ReadOnlyField label="Job Number" value={formData.job} />
            <ReadOnlyField label="Delivery Address" value={formData.address} />
          </div>
          <p className="app-muted mt-3 text-xs">
            Set when this RFQ was started. Go back to form selection to change
            it.
          </p>
        </div>
      </div>

      <CustomFormSections
        sections={formDefinition?.sections || []}
        values={formData?.custom_fields || {}}
        onFieldChange={handleFieldChange}
        onSectionChange={handleSectionChange}
      />

      {/* ── Messages & Actions ── */}
      <div className={`${CARD_CLASSES} mt-5`}>
        {hasMessages && (
          <div className="space-y-2 px-5 py-4 sm:px-6" role="status">
            {submitStatus === "success" && (
              <p className="rounded-xl bg-emerald-500/15 px-4 py-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Form submitted successfully.
              </p>
            )}
            {submitStatus === "error" && (
              <p className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300">
                Form submission failed. Please try again.
              </p>
            )}
            {requiredError && (
              <p className="rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-700 dark:text-rose-300">
                {requiredError}
              </p>
            )}
            {formID && (
              <p className="app-muted text-xs">
                Draft ID: {formID}
              </p>
            )}
          </div>
        )}

        <div
          className={`app-surface flex flex-wrap justify-center gap-3 px-5 py-4 sm:justify-end sm:px-6 ${
            hasMessages ? "border-t" : ""
          }`}
        >
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={saving}
            className="app-btn app-btn-secondary"
          >
            <svg
              aria-hidden="true"
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
            {saving ? "Saving..." : "Save Draft"}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="app-btn app-btn-primary"
          >
            <svg
              aria-hidden="true"
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
            Submit RFQ
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomWindowForm;
